import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  BackIcon,
  ThemeIcon,
  EyeIcon as PreviewIcon,
  TemplateIcon,
  AdvancedIcon,
  AlertIcon,
  SaveIcon,
  EditIcon,
  ShareIcon,
} from '../shared/Icons';
import { ADMIN_COLORS } from '../../theme';

/* This header's icons were 17px on a 24-viewBox stroke, which is the shared
   Icons.tsx default size (20) minus a bit — pin it explicitly per call below
   rather than changing the shared default and shifting every other caller. */
const HEADER_ICON_SIZE = 17;

const COLORS = {
  border: ADMIN_COLORS.border,
  borderStrong: ADMIN_COLORS.borderStrong,
  surface: ADMIN_COLORS.surface,
  text: ADMIN_COLORS.text,
  textMuted: ADMIN_COLORS.textMuted,
  textFaint: ADMIN_COLORS.textFaint,
  hover: ADMIN_COLORS.hover,
  primary: ADMIN_COLORS.accent,
  primaryHover: ADMIN_COLORS.accentHover,
  primaryDisabled: ADMIN_COLORS.accentDisabled,
  primaryRing: ADMIN_COLORS.accentRing,
  theme: '#8b5cf6',
  advanced: '#6366f1',
  problemError: '#d92d20',
  problemWarning: '#f79009',
  preview: '#0ea5e9',
  template: '#f59e0b',
  draftBg: ADMIN_COLORS.warningSoft,
  draftText: ADMIN_COLORS.warning,
  draftBorder: ADMIN_COLORS.warningBorder,
  publishedBg: ADMIN_COLORS.successSoft,
  publishedText: ADMIN_COLORS.success,
  publishedBorder: ADMIN_COLORS.successBorder,
  share: '#10b981',
};

const HeaderBar = styled.header<{ $height: number }>`
  flex-shrink: 0;
  height: ${(p) => p.$height}px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: ${COLORS.surface};
  border-bottom: 1px solid ${COLORS.border};
  z-index: 1200;
`;

const Side = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${COLORS.textMuted};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: ${COLORS.hover};
    color: ${COLORS.text};
  }
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${COLORS.text};
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const RenameButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  flex-shrink: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${COLORS.textMuted};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: ${COLORS.hover};
    color: ${COLORS.text};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TitleInput = styled.input`
  width: 460px;
  max-width: 100%;
  height: 30px;
  padding: 0 8px;
  border: 1px solid ${COLORS.primary};
  border-radius: 6px;
  outline: none;
  box-shadow: 0 0 0 3px ${COLORS.primaryRing};
  background: ${COLORS.surface};
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

interface FormTitleProps {
  name?: string;
  disabled: boolean;
  onRename?: (name: string) => Promise<void>;
}

/* Click the pencil to rename in place. Enter or blur commits, Escape cancels.
   Rename persists on its own (not through Save) so it works on a clean form. */
const FormTitle: React.FC<FormTitleProps> = ({
  name,
  disabled,
  onRename,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name || '');
  const settledRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(name || '');
  }, [name, editing]);

  const finish = async (commit: boolean) => {
    if (settledRef.current) return;
    settledRef.current = true;
    const next = draft.trim();
    setEditing(false);
    if (commit && onRename && next && next !== name) {
      try {
        await onRename(next);
      } catch {
        setDraft(name || '');
      }
    } else {
      setDraft(name || '');
    }
  };

  if (editing) {
    return (
      <TitleInput
        autoFocus
        value={draft}
        maxLength={255}
        aria-label="Form name"
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={() => finish(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') finish(true);
          if (e.key === 'Escape') finish(false);
        }}
      />
    );
  }

  return (
    <>
      <Title title={name || 'Form Builder'}>
        {name || 'Form Builder'}
      </Title>
      {onRename && (
        <RenameButton
          disabled={disabled}
          onClick={() => {
            settledRef.current = false;
            setEditing(true);
          }}
          title="Rename form"
          aria-label="Rename form"
        >
          <EditIcon size={14} />
        </RenameButton>
      )}
    </>
  );
};

/* Status reads as a soft outlined tag rather than a solid grey pill — the grey
   slab made a Draft form look disabled. */
const StatusTag = styled.span<{ $published: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  height: 22px;
  padding: 0 8px;
  border: 1px solid
    ${(p) =>
      p.$published
        ? COLORS.publishedBorder
        : COLORS.draftBorder};
  border-radius: 6px;
  background: ${(p) =>
    p.$published ? COLORS.publishedBg : COLORS.draftBg};
  color: ${(p) =>
    p.$published ? COLORS.publishedText : COLORS.draftText};
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

/* Each utility is its own bordered button — merging them into one segmented
   control made unrelated actions (Theme, Preview) read as a single toggle. */
const ProblemsButtonWrap = styled.span`
  position: relative;
  display: inline-flex;
`;

const ProblemsBadge = styled.span<{ $error: boolean }>`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border: 2px solid ${COLORS.surface};
  border-radius: 9px;
  background: ${(p) =>
    p.$error ? COLORS.problemError : COLORS.problemWarning};
  color: #ffffff;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
  pointer-events: none;
`;

const IconButton = styled.button<{ $accent: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  background: ${COLORS.surface};
  color: ${(p) => p.$accent};
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    transform 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${(p) => p.$accent};
    background: color-mix(in srgb, ${(p) => p.$accent} 8%, transparent);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    color: ${COLORS.textFaint};
    cursor: not-allowed;
  }
`;

const TemplateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  background: ${COLORS.surface};
  color: ${COLORS.text};
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  svg {
    color: ${COLORS.template};
  }

  &:hover:not(:disabled) {
    border-color: ${COLORS.borderStrong};
    background: ${COLORS.hover};
  }

  &:disabled {
    color: ${COLORS.textFaint};
    cursor: not-allowed;
  }

  &:disabled svg {
    color: ${COLORS.textFaint};
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: ${COLORS.primary};
  color: #ffffff;
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.1);
  transition:
    background 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover:not(:disabled) {
    background: ${COLORS.primaryHover};
    box-shadow: 0 2px 6px ${COLORS.primaryRing};
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    background: ${COLORS.primaryDisabled};
    box-shadow: none;
    cursor: not-allowed;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

interface BuilderHeaderProps {
  height: number;
  formName?: string;
  isPublished: boolean;
  saving: boolean;
  hasFields: boolean;
  onBack: () => void;
  onOpenTheme: () => void;
  onOpenAdvanced: () => void;
  problemCount?: number;
  hasProblemErrors?: boolean;
  onOpenProblems?: () => void;
  onPreview: () => void;
  onSaveAsTemplate: () => void;
  onSave: () => void;
  onRename?: (name: string) => Promise<void>;
  onShare?: () => void;
}

export const BuilderHeader: React.FC<
  BuilderHeaderProps
> = ({
  height,
  formName,
  isPublished,
  saving,
  hasFields,
  onBack,
  onOpenTheme,
  onOpenAdvanced,
  problemCount = 0,
  hasProblemErrors = false,
  onOpenProblems,
  onPreview,
  onSaveAsTemplate,
  onSave,
  onRename,
  onShare,
}) => (
  <HeaderBar $height={height}>
    <Side>
      <BackButton
        onClick={onBack}
        title="Back to forms"
        aria-label="Back to forms"
      >
        <BackIcon size={HEADER_ICON_SIZE} />
      </BackButton>
      <TitleGroup>
        <FormTitle
          name={formName}
          disabled={saving}
          onRename={onRename}
        />
        <StatusTag $published={isPublished}>
          {isPublished ? 'Published' : 'Draft'}
        </StatusTag>
      </TitleGroup>
    </Side>

    <Actions>
      <IconButton
        $accent={COLORS.theme}
        onClick={onOpenTheme}
        disabled={saving}
        title="Theme"
        aria-label="Theme"
        >
        <ThemeIcon size={HEADER_ICON_SIZE} />
      </IconButton>

      <IconButton
        $accent={COLORS.advanced}
        onClick={onOpenAdvanced}
        disabled={saving}
        title="Advanced settings"
        aria-label="Advanced settings"
        >
        <AdvancedIcon size={HEADER_ICON_SIZE} />
      </IconButton>

      {onOpenProblems && (
        <ProblemsButtonWrap>
          <IconButton
            $accent={
              problemCount === 0
              ? COLORS.textMuted
              : hasProblemErrors
              ? COLORS.problemError
              : COLORS.problemWarning
            }
            onClick={onOpenProblems}
            title={
              problemCount === 0
              ? 'Problems (none)'
              : `Problems (${problemCount})`
            }
            aria-label={`Problems, ${problemCount}`}
            >
            <AlertIcon size={HEADER_ICON_SIZE} />
          </IconButton>
          {problemCount > 0 && (
            <ProblemsBadge $error={hasProblemErrors}>
              {problemCount > 99 ? '99+' : problemCount}
            </ProblemsBadge>
          )}
        </ProblemsButtonWrap>
      )}

      <IconButton
        $accent={COLORS.preview}
        onClick={onPreview}
        disabled={saving || !hasFields}
        title={
          hasFields ? 'Preview' : 'Add a field to preview'
        }
        aria-label="Preview"
        >
        <PreviewIcon size={HEADER_ICON_SIZE} />
      </IconButton>

      {onShare && (
        <IconButton
          $accent={COLORS.share}
          onClick={onShare}
          disabled={saving}
          title="Share"
          aria-label="Share"
        >
          <ShareIcon size={HEADER_ICON_SIZE} />
        </IconButton>
      )}

      <TemplateButton
        onClick={onSaveAsTemplate}
        disabled={saving || !hasFields}
      >
        <TemplateIcon size={HEADER_ICON_SIZE} />
        Save as Template
      </TemplateButton>

      <SaveButton onClick={onSave} disabled={saving}>
        {saving ? (
          <Spinner />
        ) : (
          <SaveIcon size={HEADER_ICON_SIZE} />
        )}
        {saving ? 'Saving…' : 'Save'}
      </SaveButton>
    </Actions>
  </HeaderBar>
);

export default BuilderHeader;
